import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  HashtagIcon,
  EyeIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../../components/UI/ToastProvider';
import { tagService, Tag } from '../../services/tagService';
import { CreateTagModal } from '../../components/Tags/CreateTagModal';
import { EditTagModal } from '../../components/Tags/EditTagModal';
import { DeleteTagModal } from '../../components/Tags/DeleteTagModal';
import { DocumentListByFilter } from '../../components/Documents/DocumentListByFilter';
import { DocumentNode } from '../../types/documents';

export const DocumentTagsPage: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDocumentList, setShowDocumentList] = useState(false);
  const [selectedTagForDocuments, setSelectedTagForDocuments] = useState<Tag | null>(null);
  const { addToast } = useToast();

  const getTypeTranslation = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'document':
        return 'Documento';
      case 'knowledge':
        return 'Conhecimento';
      default:
        return type;
    }
  };

  const loadTags = async () => {
    try {
      setIsLoading(true);
      const data = await tagService.getAllTags();
      setTags(data);
    } catch (error) {
      addToast('Erro ao carregar tags. Tente novamente.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateTag = () => {
    setShowCreateModal(true);
  };

  const handleEditTag = (tag: Tag) => {
    setSelectedTag(tag);
    setShowEditModal(true);
  };

  const handleDeleteTag = (tag: Tag) => {
    setSelectedTag(tag);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTag) return;

    setIsDeleting(true);
    try {
      await tagService.deleteTag(selectedTag.id);
      setTags(prev => prev.filter(tag => tag.id !== selectedTag.id));
      addToast('Tag excluída com sucesso!', 'success');
      setShowDeleteModal(false);
      setSelectedTag(null);
    } catch (error) {
      addToast('Erro ao excluir tag. Tente novamente.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateSuccess = () => {
    loadTags();
  };

  const handleEditSuccess = () => {
    loadTags();
  };

  const handleViewDocuments = (tag: Tag) => {
    setSelectedTagForDocuments(tag);
    setShowDocumentList(true);
  };

  const handleBackFromDocuments = () => {
    setShowDocumentList(false);
    setSelectedTagForDocuments(null);
  };

  const handleNodeSelect = (node: DocumentNode) => {
    // Implementar navegação para o documento selecionado
    addToast('Funcionalidade de navegação será implementada', 'info');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Se estiver mostrando a listagem de documentos
  if (showDocumentList && selectedTagForDocuments) {
    return (
      <DocumentListByFilter
        filterType="tag"
        filterId={selectedTagForDocuments.id}
        filterName={selectedTagForDocuments.name}
        onBack={() => setShowDocumentList(false)}
        onNodeSelect={handleNodeSelect}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tags de Documentos</h1>
          <p className="text-gray-600">Gerencie as tags para organizar e classificar seus documentos</p>
        </div>
        <button
          onClick={handleCreateTag}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Tag
        </button>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Tags Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTags.map((tag) => (
          <div
            key={tag.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold mr-3"
                  style={{ backgroundColor: '#3B82F6' }}
                >
                  <HashtagIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">#{tag.name}</h3>
                  <p className="text-sm text-gray-600">Tag de documento</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEditTag(tag)}
                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Editar tag"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteTag(tag)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Excluir tag"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Documentos:</span>
                <span className="font-medium text-gray-900">{tag.documentCount}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Tipo:</span>
                <span className="font-medium text-gray-900">{getTypeTranslation(tag.type)}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => handleViewDocuments(tag)}
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                title="Ver documentos com esta tag"
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                Ver documentos
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredTags.length === 0 && (
        <div className="text-center py-12">
          <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma tag encontrada</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Tente ajustar os termos de busca.' : 'Comece criando sua primeira tag.'}
          </p>
        </div>
      )}

      {/* Modals */}
      <CreateTagModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditTagModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
        tag={selectedTag}
      />

      <DeleteTagModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTag(null);
        }}
        onConfirm={handleConfirmDelete}
        tag={selectedTag}
        isLoading={isDeleting}
      />
    </div>
  );
}; 